import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    Button,
    Checkbox,
    IconButton,
    Skeleton,
    Stack,
    TableBody,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTransactionRowColor } from '../../../actions/bank';
import { countDate } from '../../../actions/date';
import { showCurrentStatus } from '../../../actions/user';
import DecimalInput from '../Table/TableBodyCells/DecimalInput';
import IntegerInput from './TableBodyCells/IntegerInput';
import { useParams } from 'react-router-dom';

function renderTableCell({
    row,
    headCell,
    handleQuantityChange,
    handleBankAccountClick,
    emulation,
    roleId,
    isSelected,
    roundToTwoDecimals,
    labelId,
    t,
    i18n,
    dataSetTitle,
    handleStudentStatistics,
    studentStatId,
    setStudentStatId,
    renderAccruedForInput,
    handleAccruedForChange,
    handleClick,
    handleOpenEditQuantityModal,
    handleOpenEditProductModal,
    onDeleteProductClick,
    isRowHidden,
    onVisibilityOfProductClick,
    tooltipMap,
    renderOfficeSelect,
    handleSelectOfficeChange,
    renderAdditionalInfoInput,
    role,
}) {
    if (
    (roleId === 5 && ['counterparty', 'orderOffice'].includes(headCell.id)) ||
    (role === 'students' && ['counterparty', 'orderOffice'].includes(headCell.id))
    ) {
       return null;
    }

    if (
        headCell.id === 'teacher' &&
        ((roleId === 2 && emulation.length === 1) || (roleId === 3 && !emulation.length))
    ) {
        return <TableCell align="left">{row.teacher}</TableCell>;
    } else if (headCell.id === 'teacher') {
        return null;
    }

    if (headCell.type === 'displayOrder') {
        if ((roleId === 2 && emulation.length === 1) || (roleId === 3 && !emulation.length)) {
            return (
                <TableCell key={headCell.id} align="left" padding={headCell.disablePadding ? 'none' : 'normal'}>
                    <Typography>{t(row.promo)}</Typography>
                </TableCell>
            );
        }
        return null;
    }

    if (headCell.type === 'options') {
        if ((roleId === 2 && emulation.length === 1) || (roleId === 3 && !emulation.length)) {
            return (
                <TableCell padding="checkbox">
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            mr: '10px',
                        }}
                    >
                        <Tooltip title={t('storefrontComponent.editProductQuantity')}>
                            <IconButton onClick={() => handleOpenEditQuantityModal(row.id)}>
                                <ProductionQuantityLimitsIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('storefrontComponent.editProduct')}>
                            <IconButton onClick={() => handleOpenEditProductModal(row.id)}>
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('storefrontComponent.deleteProduct')}>
                            <IconButton onClick={() => onDeleteProductClick(row.id)}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            title={
                                isRowHidden
                                    ? t('storefrontComponent.productHidden')
                                    : t('storefrontComponent.visibleProduct')
                            }
                        >
                            <IconButton onClick={() => onVisibilityOfProductClick(row.id)}>
                                {isRowHidden ? (
                                    <VisibilityOffIcon
                                        style={{
                                            color: 'red',
                                        }}
                                    />
                                ) : (
                                    <VisibilityIcon
                                        style={{
                                            color: 'green',
                                        }}
                                    />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </TableCell>
            );
        }
        return null;
    }

    switch (headCell.type) {
        case 'string':
            return <TableCell align="left">{row[headCell.id]}</TableCell>;
        case 'group':
            if (roleId !== 3 && roleId !== 4) return null;
            return (
                <TableCell align="left">
                    {typeof row.group === 'object'
                        ? Object.values(row.group).find(
                              value => value !== null && value !== undefined && value !== ''
                          ) || ''
                        : row.group || ''}
                </TableCell>
            );
        case 'status':
            return <TableCell align="left">{showCurrentStatus(row.status, { t, i18n })}</TableCell>;
        case 'decimalInput':
            return (
                <TableCell align="left">
                    <DecimalInput
                        id={row.rowId}
                        quantity={row.quantity}
                        onChange={handleQuantityChange}
                        isSelected={isSelected}
                        t={t}
                    />
                </TableCell>
            );
        case 'integerInput':
            return (
                <TableCell align="left">
                    <IntegerInput
                        id={row.rowId}
                        quantity={row.quantity}
                        onChange={handleQuantityChange}
                        isSelected={isSelected}
                        t={t}
                    />
                </TableCell>
            );
        case 'comment':
            return (
                <TableCell align="left">
                    {renderAccruedForInput(row.rowId, row.accruedFor, handleAccruedForChange)}
                </TableCell>
            );
        case 'statistics':
            return (
                <TableCell align="left">
                    <Button
                        onClick={() => {
                            setStudentStatId(null);
                            setTimeout(() => {
                                handleStudentStatistics(row.id, `${row.name} ${row.surname}`);
                            }, 200);
                        }}
                        title={dataSetTitle}
                        color="inherit"
                        style={{
                            backgroundColor: studentStatId && studentStatId.id === row.id ? '#00a10c' : 'inherit',
                            color: studentStatId && studentStatId.id === row.id ? '#fff' : 'inherit',
                        }}
                        className="single_group_btn"
                        variant="contained"
                    >
                        {row.days_stat.value}
                    </Button>
                </TableCell>
            );
        case 'image':
            return (
                <TableCell id={labelId} scope="row" padding="none">
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                        sx={{ margin: '5px' }}
                    >
                        <img width="50" src={row.img} alt={row.productName}></img>
                        <Typography variant="body2" color="text.secondary">
                            {row.productName}
                        </Typography>
                    </Stack>
                </TableCell>
            );
        case 'img':
            return (
                <TableCell
                    align="left"
                    style={{
                        display: 'flex',
                        alignContent: 'center',
                    }}
                >
                    <img
                        src={row.img}
                        alt="Product Image"
                        style={{
                            width: '70px',
                            height: '70px',
                            borderRadius: '7px',
                            objectFit: 'cover',
                        }}
                    />
                </TableCell>
            );
        case 'sum':
            return <TableCell align="left">{`${roundToTwoDecimals(row.price * row.quantity)} SBK`}</TableCell>;
        case 'link':
            return (
                <TableCell align="left">
                    <Tooltip title="SOROBANK" placement="bottom">
                        <Button
                            onClick={() => handleBankAccountClick(row.franchId || row.rowId)}
                            style={{ height: '40px', borderRadius: '20px', fontSize: '14px', color: '#58a315' }}
                            aria-label="SOROBANK"
                            startIcon={<AccountBalanceIcon style={{ color: '#58a315' }} />}
                        >
                            SOROBANK
                        </Button>
                    </Tooltip>
                </TableCell>
            );
        case 'checkbox':
            return (
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        onClick={event => handleClick(event, row.rowId)}
                        checked={isSelected(row.rowId)}
                        inputProps={{ 'aria-labelledby': labelId }}
                    />
                </TableCell>
            );
        case 'amount':
            return <TableCell align="left">{row.amount || 0}</TableCell>;
        case 'vip':
            return <TableCell align="left">{+row.price !== +row.vip ? row.vip : null}</TableCell>;
        case 'createdDate':
            return <TableCell align="left">{countDate(row[headCell.id], true)}</TableCell>;
        case 'product_type':
            return (
                <TableCell align="left">
                    {row[headCell.id] === 'educational'
                        ? t('modalAddProduct.educational')
                        : t('modalAddProduct.souvenir')}
                </TableCell>
            );
        case 'selectOffice':
            return (
                <TableCell align="left">
                    {renderOfficeSelect(row.rowId, row.selectOffice, handleSelectOfficeChange)}
                </TableCell>
            );
        case 'additionalInfo':
            return <TableCell align="left">{renderAdditionalInfoInput(row.rowId)}</TableCell>;
        case 'stringWithTooltip':
            const tooltipArray = tooltipMap[row.rowId] || tooltipMap[row.id] || [];

            const tooltipText =
                tooltipArray.length > 0
                    ? tooltipArray.join('\n')
                    : row.rowId
                    ? t('storefrontComponent.noOrdersPending')
                    : null;

            return (
                <TableCell align="left">
                    <Tooltip title={tooltipText} placement="top">
                        <span>{row[headCell.id]}</span>
                    </Tooltip>
                </TableCell>
            );
        default:
            return <TableCell align="left">{row[headCell.id]}</TableCell>;
    }
}

export default function BankTableBody(props) {
    const {
        rows,
        headCells,
        isSelected,
        handleClick,
        handleQuantityChange,
        handleBankAccountClick,
        emulation,
        roleId,
        roundToTwoDecimals,
        studentStatId,
        setStudentStatId,
        handleStudentStatistics,
        renderAccruedForInput,
        handleAccruedForChange,
        handleOpenEditQuantityModal,
        handleOpenEditProductModal,
        onDeleteProductClick,
        hiddenRows,
        onVisibilityOfProductClick,
        order,
        orderBy,
        renderOfficeSelect,
        handleSelectOfficeChange,
        renderAdditionalInfoInput,
        fetching,
        tooltipMap,
    } = props;

    const { t, i18n } = useTranslation();
    const [visibleRows, setVisibleRows] = useState(rows);
    const { role } = useParams();

    useEffect(() => {
        setVisibleRows(rows);
    }, [rows]);

    const getComparator = (order, orderBy) => {
        const dir = order === 'asc' ? 1 : -1;

        return (a, b) => {
            if (orderBy === 'product_type') {
                const translatedA = t(`modalAddProduct.${a.product_type ?? 'souvenir'}`) || '';
                const translatedB = t(`modalAddProduct.${b.product_type ?? 'souvenir'}`) || '';
                return translatedA.localeCompare(translatedB, i18n.language, { sensitivity: 'base' }) * dir;
            }

            if (orderBy === 'status') {
                const statusA = showCurrentStatus(parseFloat(a[orderBy]), { t, i18n });
                const statusB = showCurrentStatus(parseFloat(b[orderBy]), { t, i18n });
                return statusA.localeCompare(statusB) * dir;
            }

            if (orderBy === 'group') {
                const getGroupValue = group =>
                    typeof group === 'object'
                        ? Object.values(group).find(v => v !== null && v !== undefined && v !== '') || ''
                        : group || '';

                const groupA = getGroupValue(a.group)
                    .replace(/[^a-zA-Zа-яА-Я0-9]/g, '')
                    .toLowerCase();
                const groupB = getGroupValue(b.group)
                    .replace(/[^a-zA-Zа-яА-Я0-9]/g, '')
                    .toLowerCase();
                return groupA.localeCompare(groupB) * dir;
            }

            if (orderBy === 'sum') {
                const sumA = roundToTwoDecimals(a.price * a.quantity);
                const sumB = roundToTwoDecimals(b.price * b.quantity);
                return (sumA - sumB) * dir;
            }

            if (orderBy === 'createdDate') {
                return (new Date(a[orderBy]).getTime() - new Date(b[orderBy]).getTime()) * dir;
            }

            const numA = Number(a[orderBy]);
            const numB = Number(b[orderBy]);
            if (!isNaN(numA) && !isNaN(numB)) {
                return (numA - numB) * dir;
            }

            return (
                String(a[orderBy]).localeCompare(String(b[orderBy]), undefined, {
                    sensitivity: 'base',
                    numeric: true,
                }) * dir
            );
        };
    };

    const sortedRows = useMemo(() => {
        if (order && orderBy) {
            return [...visibleRows].sort(getComparator(order, orderBy));
        }
        return visibleRows;
    }, [visibleRows, order, orderBy]);

    return (
        <>
            {fetching &&
                Array(visibleRows.length || 10)
                    .fill(0)
                    .map((e, key) => (
                        <TableRow key={key}>
                            {headCells.map((headCell, index) => (
                                <TableCell key={`${key}-${index}`}>
                                    <Skeleton />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}

            {!sortedRows.length && !fetching && (
                <Typography variant="body2" align="center">
                    {t('text.noDataYetMessage')}
                </Typography>
            )}
            <TableBody>
                {!fetching &&
                    sortedRows?.map((row, index) => {
                        const isRowHidden = hiddenRows ? hiddenRows.map(item => item.id).includes(row.id) : null;
                        const isItemSelected = isSelected ? isSelected(row.rowId) : null;
                        const labelId = `enhanced-table-checkbox-${index}`;
                        let dataSetTitle = '';

                        if (row.days_stat) {
                            for (let i in row.days_stat.dataSet) {
                                const d = new Date(i);
                                const _month = d.getMonth() + 1 < 10 ? '0' + (d.getMonth() + 1) : d.getMonth() + 1;
                                const _date = d.getDate() < 10 ? '0' + d.getDate() : d.getDate();
                                dataSetTitle += `${_date}.${_month}.${d.getFullYear()} - ${
                                    row.days_stat.dataSet[i]
                                }%\n`;
                            }
                        }

                        return (
                            <TableRow
                                hover
                                role="checkbox"
                                aria-checked={isItemSelected}
                                tabIndex={-1}
                                key={row.rowId || row.id}
                                selected={isItemSelected}
                                sx={{ cursor: 'pointer' }}
                                style={{
                                    transition: 'all 0.2s',
                                    backgroundColor: getTransactionRowColor(row),
                                }}
                            >
                                {headCells.map(headCell => (
                                    <React.Fragment key={headCell.id}>
                                        {renderTableCell({
                                            row,
                                            headCell,
                                            handleQuantityChange,
                                            handleBankAccountClick,
                                            emulation,
                                            roleId,
                                            isSelected,
                                            roundToTwoDecimals,
                                            labelId,
                                            t,
                                            i18n,
                                            dataSetTitle,
                                            handleStudentStatistics,
                                            studentStatId,
                                            setStudentStatId,
                                            renderAccruedForInput,
                                            handleAccruedForChange,
                                            handleClick,
                                            handleOpenEditQuantityModal,
                                            handleOpenEditProductModal,
                                            onDeleteProductClick,
                                            isRowHidden,
                                            onVisibilityOfProductClick,
                                            renderOfficeSelect,
                                            handleSelectOfficeChange,
                                            renderAdditionalInfoInput,
                                            tooltipMap,
                                            role,
                                        })}
                                    </React.Fragment>
                                ))}
                            </TableRow>
                        );
                    })}
            </TableBody>
        </>
    );
}
